import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "./ui/Button";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface crashes to the browser console in development. In production,
    // hook this up to an error-reporting service (Sprint 7 hardening).
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;
    if (error) {
      if (this.props.fallback) return this.props.fallback(error, this.reset);
      return (
        <main className="flex min-h-screen items-center justify-center bg-neutral-50 px-6">
          <div className="max-w-md rounded-3xl border border-neutral-100 bg-white p-8 text-center shadow-soft">
            <h1 className="font-display text-xl font-semibold text-neutral-900">
              Something went wrong
            </h1>
            <p className="mt-2 text-sm text-neutral-600">
              We hit an unexpected error while rendering this page.
            </p>
            <Button onClick={this.reset} className="mt-6">
              Try again
            </Button>
          </div>
        </main>
      );
    }
    return this.props.children;
  }
}
